import { useMemo } from 'react'
import { useQueryParams } from 'src/hooks/useQueryParams'
import { PERIOD_OPTIONS_TO_LABELS } from 'src/constants/filterPeriods'
import { FilterPeriod } from 'src/constants/filterPeriods/enums/filterPeriod.enum'
import { GlobalQueryParams } from 'src/interfaces/globalQueryParams.interface'
import { ReviewAnalysisDateRange } from 'src/services/reviewAnalysisApi/interfaces/reviewAnalysis.interface'
import { getDateRangeByQueryParams } from 'src/utils/getDateRangeByQueryParams'

// Reads the global top-bar date filter (?from/?to/?period) and returns it as the
// ReviewAnalysisDateRange the review-analysis query hooks accept — or undefined
// when no range is active (e.g. the "Lifetime" preset), so the request carries
// no date params and the dashboard shows full history.
export const useReviewAnalysisDateRange = ():
  | ReviewAnalysisDateRange
  | undefined => {
  const { queryParams } = useQueryParams<GlobalQueryParams>()
  // Lifetime is no window, even though the top bar writes the campaign's
  // whole active range into ?from/?to for its own display. Sending that range
  // made every "vs previous period" compare against the empty stretch before
  // the first review, so no change ever showed. Without a range the backend
  // covers all reviews and compares the last 30 days with the 30 before.
  const isLifetime = queryParams.period === FilterPeriod.LIFETIME
  const dates = isLifetime ? undefined : getDateRangeByQueryParams(queryParams)
  const start = dates?.start
  const end = dates?.end

  return useMemo(() => {
    if (!start || !end) return undefined
    return { from: start, to: end }
  }, [start, end])
}

// The active date filter as query params to carry on in-feature navigation, so
// the selected range (incl. a Custom from/to that lives only in the URL) is not
// dropped when moving between brand/facility/platform pages. Typed as a loose
// record so it can be spread straight into createLink's queryParams; qs drops
// undefined keys, so an inactive filter contributes nothing.
export const useReviewAnalysisLinkQuery = (): Record<
  string,
  string | undefined
> => {
  const { queryParams } = useQueryParams<GlobalQueryParams>()
  return {
    from: queryParams.from,
    period: queryParams.period,
    to: queryParams.to,
  }
}

// The top-bar period as a card subtitle: "Last 30 days", "Lifetime", or
// "Selected period" for a custom from/to.
export const useReviewAnalysisPeriodLabel = (): string => {
  const { queryParams } = useQueryParams<GlobalQueryParams>()
  const period = queryParams.period as FilterPeriod | undefined
  if (!period) return PERIOD_OPTIONS_TO_LABELS[FilterPeriod.LIFETIME]
  if (period === FilterPeriod.CUSTOM) return 'Selected period'
  return PERIOD_OPTIONS_TO_LABELS[period] ?? 'Selected period'
}
